import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { ntxAPI } from '@API/axiosNeatex';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { GetPrintTemplateByType } from '@store/NTX/PrintTemplate/PrintTemplateSlice';
import NtxCustomModal from '../Modal/NtxCustomModal';
import Loading from '@components/Shared/Loader/Loading';

type Props = {
  PrintTemplateTypeId: string;
  ReferenceId: string;
};

const ListPrint = ({ PrintTemplateTypeId, ReferenceId }: Props) => {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();

  // State for modal visibility, PDF URL, loading status, and errors
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    dispatch(GetPrintTemplateByType(PrintTemplateTypeId));
  }, [PrintTemplateTypeId, dispatch]);

  const {
    PrintTemplateByType: data,
    loading: loading1,
    error: error1
  } = useAppSelector(state => state.PrintTemplate);

  const handlePrint = async (TemplateId: string, parameterList: any[]) => {
    setLoading(true); // Start loading
    setError(null); // Reset error
    setModalOpen(true); // Open modal

    try {
      const response = await ntxAPI.post(
        'Reports/Print',
        {
          id: TemplateId,
          parameterList: [
            {
              id: parameterList[0].id,
              value: ReferenceId
            }
          ]
        },
        {
          responseType: 'blob'
        }
      );

      const blob = new Blob([response.data], {
        type: 'application/pdf' // Adjust MIME type for PDF
      });

      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url); // Store the PDF URL
    } catch (err) {
      setError(t('حدث خطأ أثناء تحميل الملف.')); // Set error message
      console.error('Error generating file:', err);
    } finally {
      setLoading(false); // Stop loading
    }
  };

  return (
    <>
      <Loading loading={loading1} error={error1} Type='Dots'>
        <div className='list-group' style={{ maxWidth: '400px' }}>
          {data?.map(m => (
            <button
              key={m.id}
              className='list-group-item list-group-item-action d-flex justify-content-between align-items-center'
              onClick={() => handlePrint(m.id, m.parameterList)}
            >
              {i18n.language === 'en' ? m.name : m.name2}
              <i className={`${m.icon ? m.icon : 'fas fa-print'} text-theme`}></i>
            </button>
          ))}
        </div>
      </Loading>

      {/* Modal */}
      <NtxCustomModal
        width='98%'
        show={isModalOpen}
        setShow={setModalOpen}
        showHeader={false}
        centered={true}
        bodyClassName='p-0'
      >
        <div style={{ height: '90vh', paddingBottom: 3 }} className='border border-1 border-theme '>
          {/* Loading State */}
          {loading && (
            <div
              className='d-flex justify-content-center align-items-center'
              style={{
                margin: 0,
                height: 'calc(100% - 49px)'
              }}
            >
              <div className='spinner-grow text-theme' role='status'>
                <span className='visually-hidden'>{t('تحميل')}</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {!loading && error && (
            <div
              className='d-flex justify-content-center align-items-center text-danger'
              style={{
                margin: 0,
                height: 'calc(100% - 49px)'
              }}
            >
              <p>{error}</p>
            </div>
          )}

          {/* PDF Viewer */}
          {!loading && !error && pdfUrl && (
            <iframe
              src={pdfUrl}
              width='100%'
              height='100%'
              title='PDF Preview'
              style={{
                margin: 0,
                height: 'calc(100% - 50px)',
                // boxShadow: '0 4px 8px rgba(0, 0, 0, 0.4)', // الظل
                boxShadow: '0 1px 10px var(--bs-theme)',
                border: '1px solid #ccc' // خط خارجي اختياري
                // borderRadius: '8px', // زوايا مستديرة اختيارية
              }}
            ></iframe>
          )}

          {/* Modal Footer */}
          <div className='text-center'>
            <button
              className='btn btn-lg btn-theme w-25 rounded-0'
              onClick={() => {
                setModalOpen(false);
                setPdfUrl(null); // Reset PDF URL
                setError(null); // Reset error
              }}
            >
              {t('إلغاء')}
            </button>
          </div>
        </div>
      </NtxCustomModal>
    </>
  );
};

export default ListPrint;
